/**
 * What an administrator pastes to link an Aurora design: the design's id, and
 * the Aurora project's when it came from a CAD URL. The project in a URL is
 * only a claim; the server takes the project from Aurora's design.
 */
export type AuroraDesignRef = {
  designId: string
  projectId: string | null
}

const UUID = `[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}`
const BARE_UUID = new RegExp(`^${UUID}$`, `i`)
const CAD_PATH = new RegExp(
  `^/projects/(${UUID})/designs/(${UUID})(?:/cad)?/?$`,
  `i`,
)
const AURORA_HOST = /(^|\.)aurorasolar\.com$/i
// Zero-width characters copied along with a link aren't visible to trim.
const INVISIBLE = /[\u200B-\u200D\u2060\uFEFF]/g

const parseUrl = (input: string): URL | null => {
  try {
    // A URL copied without its scheme still names the host.
    return new URL(/^https?:\/\//i.test(input) ? input : `https://${input}`)
  } catch {
    return null
  }
}

/**
 * Reads a bare Aurora design UUID, or an Aurora CAD URL
 * (`…aurorasolar.com/projects/{projectId}/designs/{designId}`, with or without
 * `/cad`, a query or a trailing slash). Ids come back lowercase, as Aurora
 * writes them. Anything else is null.
 */
export const parseAuroraDesignRef = (input: string): AuroraDesignRef | null => {
  const trimmed = input.replace(INVISIBLE, ``).trim()
  if (BARE_UUID.test(trimmed)) {
    return { designId: trimmed.toLowerCase(), projectId: null }
  }

  const url = parseUrl(trimmed)
  if (!url || !AURORA_HOST.test(url.hostname)) return null
  const match = CAD_PATH.exec(url.pathname)
  if (!match) return null

  return { designId: match[2].toLowerCase(), projectId: match[1].toLowerCase() }
}
